/**
 * The Module x Action grid behind the role editor.
 *
 * Kept apart from the screen because this is the part that can be wrong without looking wrong: a
 * half-applied toggle, a row that reports "all" while one box is clear, an edit that silently
 * drops the grants the catalogue does not currently list. The API replaces a role's grants
 * wholesale with exactly what it is sent, so whatever this produces *is* the role — there is no
 * merge on the server to cover a mistake here.
 */

export interface PermissionPair {
  module: string;
  action: string;
}

/** A grant as its wire form, `Module:Action` — the same shape the session and menu use. */
export type GrantKey = string;

export function grantKey(module: string, action: string): GrantKey {
  return `${module}:${action}`;
}

export function toGrantSet(permissions: readonly PermissionPair[]): Set<GrantKey> {
  return new Set(permissions.map((p) => grantKey(p.module, p.action)));
}

/**
 * Back to the wire, in a stable order.
 *
 * Sorted so that two edits producing the same set produce the same request — which is what makes
 * "did this actually change anything?" answerable by comparing, rather than by trusting.
 */
export function toPermissionList(grants: ReadonlySet<GrantKey>): PermissionPair[] {
  return [...grants]
    .map((key) => {
      const [module = '', action = ''] = key.split(':');

      return { module, action };
    })
    .filter((pair) => pair.module !== '' && pair.action !== '')
    .sort((a, b) => a.module.localeCompare(b.module) || a.action.localeCompare(b.action));
}

export function has(grants: ReadonlySet<GrantKey>, module: string, action: string): boolean {
  return grants.has(grantKey(module, action));
}

export function toggle(
  grants: ReadonlySet<GrantKey>,
  module: string,
  action: string,
): Set<GrantKey> {
  const next = new Set(grants);
  const key = grantKey(module, action);

  if (!next.delete(key)) {
    next.add(key);
  }

  return next;
}

/**
 * Grants or clears a whole module at once.
 *
 * Granting a module means granting every action the catalogue knows about, including any added
 * after this role was last edited — which is deliberate, and the reason the catalogue is fetched
 * rather than hardcoded.
 */
export function setModule(
  grants: ReadonlySet<GrantKey>,
  module: string,
  actions: readonly string[],
  granted: boolean,
): Set<GrantKey> {
  const next = new Set(grants);

  for (const action of actions) {
    if (granted) {
      next.add(grantKey(module, action));
    } else {
      next.delete(grantKey(module, action));
    }
  }

  return next;
}

export type RowState = 'none' | 'some' | 'all';

export function moduleState(
  grants: ReadonlySet<GrantKey>,
  module: string,
  actions: readonly string[],
): RowState {
  if (actions.length === 0) {
    return 'none';
  }

  const held = actions.filter((action) => has(grants, module, action)).length;

  if (held === 0) {
    return 'none';
  }

  return held === actions.length ? 'all' : 'some';
}

/**
 * The grants a role holds that the catalogue no longer lists.
 *
 * A role's rows are data and outlive the code that defined them: a module removed from the enum
 * leaves rows behind, and the editor would quietly delete them on the next save because the grid
 * cannot show what it has no column for. Surfacing the count is the honest alternative — the
 * screen warns instead of discarding silently.
 */
export function unlistedGrants(
  grants: ReadonlySet<GrantKey>,
  modules: readonly string[],
  actions: readonly string[],
): GrantKey[] {
  const knownModules = new Set(modules);
  const knownActions = new Set(actions);

  return [...grants].filter((key) => {
    const [module = '', action = ''] = key.split(':');

    return !knownModules.has(module) || !knownActions.has(action);
  });
}

/** A count for the list column — the honest measure of how much a role can do. */
export function grantCount(permissions: readonly PermissionPair[]): number {
  return toGrantSet(permissions).size;
}
